import React, { ReactNode } from 'react';

import { CheckOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--line-gray);
  max-height: 200px;
  overflow-y: auto;
  padding: 10px 20px;
`;

const ItemsTitle = styled.span`
  font-family: var(--font-bold);
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 10px;

  span {
    color: var(--dark-blue);
    font-size: 12px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  &.padded {
    padding-left: 24px;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.4;
    pointer-events: none;
  }
`;

const ColorDot = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  margin-right: 8px;
`;

interface Props {
  options: any[];
  open: boolean;
  selectedItems: any[];
  nonSelectedItems: any[];
  setSelectedItems: (v: any[]) => void;
  maxSelectable?: number;
  forceDisableSelectAll?: boolean;
  hideSelectAll?: boolean;
}

const CMASummaryFilterContractSelectionSelect = ({
  options,
  open,
  selectedItems,
  nonSelectedItems,
  setSelectedItems,
  maxSelectable,
  forceDisableSelectAll = false,
  hideSelectAll = false,
}: Props) => {
  const handleSelectItem = (item: any, disabled: boolean) => {
    if (disabled) {
      return;
    }
    setSelectedItems([...selectedItems, item]);
  };

  const handleDeselectItem = (item: any) => {
    setSelectedItems(selectedItems.filter((i) => i.value !== item.value));
  };

  const handleSelectAll = () => {
    if (forceDisableSelectAll || maxSelectable === 0) {
      return;
    }

    if (typeof maxSelectable === 'number' && maxSelectable > 0) {
      setSelectedItems([...options].slice(0, maxSelectable));
      return;
    }

    setSelectedItems([...options]);
  };

  const handleSelectNone = () => {
    setSelectedItems([]);
  };

  const reachedMaxSelections = typeof maxSelectable === 'number' && selectedItems.length >= maxSelectable;

  const disableSelectAll = forceDisableSelectAll || nonSelectedItems.length === 0 || reachedMaxSelections;
  const disableSelectNone = selectedItems.length === 0;

  return (
    <Container>
      {selectedItems.length > 0 && (
        <DropdownItemsList
          title="SELECTED CONTRACTS"
          items={selectedItems}
          icon={<CheckOutlined style={{ color: 'var(--positive-green)' }} />}
          onSelect={handleDeselectItem}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          disableSelectAll={disableSelectAll}
          disableSelectNone={disableSelectNone}
          disableItems={false}
          hideSelectAll={hideSelectAll}
        />
      )}
      {nonSelectedItems.length > 0 && (
        <DropdownItemsList
          title="UNSELECTED CONTRACTS"
          items={nonSelectedItems}
          onSelect={(item) => handleSelectItem(item, reachedMaxSelections)}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          disableSelectAll={disableSelectAll}
          disableSelectNone={disableSelectNone}
          disableItems={reachedMaxSelections}
          hideSelectAll={hideSelectAll}
        />
      )}
    </Container>
  );
};

interface DropdownItemsListProps {
  title: string;
  items: any[];
  icon?: ReactNode;
  onSelect: (item: any) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  disableSelectAll: boolean;
  disableSelectNone: boolean;
  disableItems?: boolean;
  hideSelectAll?: boolean;
}

const DropdownItemsList = ({
  title,
  items,
  icon,
  onSelect,
  onSelectAll,
  onSelectNone,
  disableSelectAll,
  disableSelectNone,
  disableItems = false,
  hideSelectAll = false,
}: DropdownItemsListProps) => {
  return (
    <Items>
      <ItemsTitle>
        {title}
        <SelectionActions>
          {!hideSelectAll && (
            <span
              onClick={!disableSelectAll ? onSelectAll : undefined}
              className={classNames({ disabled: disableSelectAll })}
            >
              Select All
            </span>
          )}
          <span
            onClick={!disableSelectNone ? onSelectNone : undefined}
            className={classNames({ disabled: disableSelectNone })}
          >
            Select None
          </span>
        </SelectionActions>
      </ItemsTitle>
      <ItemsList>
        {items.map((item) => (
          <Item
            key={item.value}
            className={classNames({ padded: !icon, disabled: disableItems })}
            onClick={!disableItems ? () => onSelect(item) : undefined}
          >
            {icon}
            <div>
              {item?.color && <ColorDot color={item.color}>●</ColorDot>}
              <span>{item.label}</span>
            </div>
          </Item>
        ))}
      </ItemsList>
    </Items>
  );
};

export default CMASummaryFilterContractSelectionSelect;
